import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { dailyRows, dayAxisProps, hasValues, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface DailyDeltaChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
  unit?: string;
}

function DeltaTooltip({ active, payload, label, unit }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0].payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((bar: any) => {
        const spread = row[`${bar.name}Spread`];
        const min = row[`${bar.name}Min`];
        const max = row[`${bar.name}Max`];

        return (
          <div key={bar.name} className="mt-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bar.color }} />
              <span className="text-gray-600">{bar.name}:</span>
              <span className="font-medium text-gray-900">{spread != null ? `${spread} ${unit}` : '—'}</span>
            </div>
            {min != null && (
              <div className="text-gray-400 ml-3.5">
                Min: {min} {unit} / Max: {max} {unit}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const DailyDeltaChart: React.FC<DailyDeltaChartProps> = ({ title, series, range, unit = '°C' }) => {
  const { t } = useTranslation();

  const dailyData = React.useMemo(() => dailyRows(series, range), [series, range]);
  const hasData = hasValues(series);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: title.toUpperCase() })}`}
            description={`${t('f-sensor-daily-averages-unavailable')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...dayAxisProps} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip content={<DeltaTooltip unit={unit} />} />
              {series.map((s) => (
                <Bar
                  key={s.name}
                  dataKey={`${s.name}Spread`}
                  name={s.name}
                  fill={s.strokeColor || '#22c55e'}
                  barSize={20}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyDeltaChart;
